import Countries from "@data/generated/countries.json";
import Meta from "@data/generated/pricing-meta.json";
import type { EligibleAffiliate } from "@interfaces/LandingPricing";
import type {
  CountryContent,
  CountryEntry,
  PricingMeta,
} from "@interfaces/PricingData";

/**
 * Read access to the small, generated pricing files. Import this (never the
 * raw `@data/landing-pricing.json`) from anything under `src/`: the raw file
 * is ~5.8 MB and would land in the browser bundle of every page.
 *
 * Products live in `@utilities/product-data` on purpose, so that only the
 * pages that list plans pull the 3 MB products file into their chunk.
 */

const countries = Countries as unknown as Record<string, CountryEntry>;
const meta = Meta as unknown as PricingMeta;

export const getCountryCodes = (): string[] => Object.keys(countries);

export const getCountryEntry = (iso2: string): CountryEntry | undefined =>
  countries[iso2.toUpperCase()];

/**
 * Country resolved for `language`, falling back to the English name when
 * that language has no translation (same behaviour as the raw file had).
 */
export const getCountryContent = (
  iso2: string,
  language: string = "en",
): CountryContent | undefined => {
  const entry = getCountryEntry(iso2);
  if (!entry) return undefined;
  const { names, ...rest } = entry;
  return {
    ...rest,
    country_name: names[language.toLowerCase()] ?? names.en ?? "",
  };
};

export const getPricingMeta = (): PricingMeta => meta;

export const getEligibleAffiliates = (): EligibleAffiliate[] =>
  meta.eligible_affiliates ?? [];

export const getUnlimitedCountryCodes = (): string[] =>
  meta.unlimited_country_codes ?? [];
